export const BRAND_POINT_ITEM_REDEMPTION_DISCOUNT_TYPE = 71
export const BRAND_POINT_ORDER_DEDUCTION_DISCOUNT_TYPE = 72

type ReceiptNumericValue = number | string

export interface BrandPointReceiptItem {
  brandPointRedeemed?: boolean | null
  brandPointRedeemPoints?: ReceiptNumericValue | null
  brandPointOriginalPriceCent?: ReceiptNumericValue | null
  count?: ReceiptNumericValue | null
  itemQuantity?: ReceiptNumericValue | null
  itemPrice?: ReceiptNumericValue | null
  salePrice?: ReceiptNumericValue | null
  remark?: string | null
  remarks?: string | null
}

export interface BrandPointReceiptDiscount {
  discountType?: number | string | null
  discountDescription?: string | null
}

function normalizeNonNegativeInteger(value: unknown) {
  const numberValue = Number(value ?? 0)
  if (!Number.isFinite(numberValue)) return 0
  return Math.max(Math.round(numberValue), 0)
}

export function normalizeReceiptRemark(value: unknown) {
  return typeof value === "string" ? value.trim() : ""
}

export function getReceiptItemRemark(item: BrandPointReceiptItem) {
  return normalizeReceiptRemark(item.remark || item.remarks)
}

export function getReceiptItemQuantity(item: BrandPointReceiptItem) {
  return normalizeNonNegativeInteger(item.count ?? item.itemQuantity)
}

export function getReceiptItemOriginalUnitPriceCent(item: BrandPointReceiptItem) {
  if (item.brandPointRedeemed) {
    return normalizeNonNegativeInteger(
      item.brandPointOriginalPriceCent ?? item.salePrice ?? item.itemPrice,
    )
  }
  return normalizeNonNegativeInteger(item.salePrice ?? item.itemPrice)
}

export function buildReceiptItemVariantKey(item: BrandPointReceiptItem) {
  const remark = getReceiptItemRemark(item)
  return [
    item.brandPointRedeemed ? "point" : "cash",
    normalizeNonNegativeInteger(item.brandPointRedeemPoints),
    getReceiptItemOriginalUnitPriceCent(item),
    `${remark.length}:${remark}`,
  ].join("|")
}

function pointRedemptionLabel(items: BrandPointReceiptItem[]) {
  const pointItems = items.filter(item => item.brandPointRedeemed)
  if (!pointItems.length) return "积分兑商品"

  const totalQuantity = pointItems.reduce(
    (total, item) => total + getReceiptItemQuantity(item),
    0,
  )
  const unitPoints = new Set(
    pointItems
      .map(item => normalizeNonNegativeInteger(item.brandPointRedeemPoints))
      .filter(points => points > 0),
  )
  if (unitPoints.size === 1 && totalQuantity > 0) {
    const [points] = unitPoints
    return `积分兑商品（${points}积分×${totalQuantity}）`
  }

  const totalPoints = pointItems.reduce(
    (total, item) => total
      + normalizeNonNegativeInteger(item.brandPointRedeemPoints)
      * getReceiptItemQuantity(item),
    0,
  )
  return totalPoints > 0
    ? `积分兑商品（共${totalPoints}积分）`
    : "积分兑商品"
}

function pointDeductionLabel(description: string | null | undefined) {
  const normalized = normalizeReceiptRemark(description)
  const pointsMatch = normalized.match(/([\d,]+)\s*积分/)
  if (!pointsMatch) return "积分抵现"
  return `积分抵现（${pointsMatch[1]!.replaceAll(",", "")}积分）`
}

export function formatBrandPointReceiptDiscountLabel(
  discount: BrandPointReceiptDiscount,
  items: BrandPointReceiptItem[],
) {
  const discountType = Number(discount.discountType)
  if (discountType === BRAND_POINT_ITEM_REDEMPTION_DISCOUNT_TYPE) {
    return pointRedemptionLabel(items)
  }
  if (discountType === BRAND_POINT_ORDER_DEDUCTION_DISCOUNT_TYPE) {
    return pointDeductionLabel(discount.discountDescription)
  }
  return normalizeReceiptRemark(discount.discountDescription)
}
